import Axios from "axios";
import AxiosConfig from "src/utilities/AxiosConfig";
import type {
  FootageRestrictedLocateConfig,
  TableResults,
  Response,
  Utility,
} from "./FootageRestrictedLocateMaintenance";

import * as yup from "yup";

export const FootageRestrictedLocateConfigSchema = yup.object().shape({
  Id: yup.number().required(),
  UtilityId: yup.number().nullable().required().label("Utility"),
  ResponseId: yup.number().nullable().required().label("Default Response"),
  NoBillLocateID: yup.number().nullable().required().label("No Bill Response"),
  NoPlantBillable: yup.boolean().nullable().label("No Plant Billable"),
  OverflowToHourly: yup.boolean().nullable().label("Overflow To Hourly"),
  HourlyResponseID: yup.number().nullable(),
  HourlyMinimum: yup
    .number()
    .lessThan(10000)
    .nullable()
    .label("Hourly Minimum"),
  MinBillableMinutes: yup
    .number()
    .lessThan(10000)
    .nullable()
    .label("Minimum Billable Minutes"),
  MaxLocates: yup.number().lessThan(10000).nullable().label("Max Locates"),
  MaxFootage: yup.number().lessThan(10000).nullable().label("Max Footage"),
  FootagePerLocate: yup
    .number()
    .lessThan(10000)
    .required()
    .label("Footage Per Locate"),
  IsActive: yup.boolean().nullable(),
  DateDeactivated: yup.string().nullable(),
  DeactivatedBy: yup.number().nullable(),
  CreatedDate: yup.string().nullable(),
  CreatedBy: yup.number().nullable(),
  DateModified: yup.string().nullable(),
  ModifiedBy: yup.number().nullable(),
  DateDeleted: yup.string().nullable(),
  DeletedBy: yup.number().nullable(),
  Comments: yup.string().max(255),
  NoBillHourlyResponseID: yup.number().nullable(),
  ContractId: yup.number().required(),
});

const API_URL = process.env.REACT_APP_API_URL;

class StatusError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

/**
 * Fetch all FootageRestrictedLocateConfigData. Includes inactive rows.
 * @returns {Promise<TableResults[]>} A promise that resolves to an array of TableResults objects.
 */
export async function getFootageRestrictedLocateConfigData(): Promise<
  TableResults[]
> {
  try {
    const response = await Axios.get(
      `${API_URL}/api/company/footagerestrictedlocateconfigs`,
      AxiosConfig.configValue("min")
    );
    // TODO add inactive filter to api instead of handling client side
    return response.data;
  } catch (error) {
    console.error("getFootageRestrictedLocateConfigData/api ~ error:", error);
    throw new StatusError("Unable to fetch config", 500);
  }
}

/**
 * Update the specified FootageRestrictedLocateConfigData.
 * @param {FootageRestrictedLocateConfig} updatedConfig - The updated configuration object.
 * @returns {Promise<number>} A promise that resolves to the HTTP status code.
 */
export async function updateFootageRestrictedLocateConfigData(
  updatedConfig: FootageRestrictedLocateConfig
): Promise<number> {
  // Validate the updatedConfig object
  await FootageRestrictedLocateConfigSchema.validate(updatedConfig, {
    abortEarly: false,
  }).catch(function (errors) {
    throw errors;
  });

  try {
    const response = await Axios.patch(
      `${API_URL}/api/company/footagerestrictedlocateconfigs/${updatedConfig.Id}`,
      updatedConfig,
      AxiosConfig.configValue("min")
    );
    return response.status;
  } catch (error) {
    console.error(
      "updateFootageRestrictedLocateConfigData/api ~ error:",
      error
    );
    throw new StatusError("Unable to update config.", 500);
  }
}

/**
 * Create a new FootageRestrictedLocateConfigData row. Most of the row is blank untill filled in.
 * @returns {Promise<TableResults | null>} A promise that resolves to a TableResults object, or null if there was an error.
 */
export async function createFootageRestrictedLocateConfigData(): Promise<TableResults | null> {
  try {
    const response = await Axios.post(
      `${API_URL}/api/company/footagerestrictedlocateconfigs`,
      {},
      AxiosConfig.configValue("min")
    );
    return response.data;
  } catch (error) {
    console.error(
      "createFootageRestrictedLocateConfigData/api ~ error:",
      error
    );
    throw new StatusError("Unable to create new config.", 500);
  }
}

/**
 * Fetch all currently active responses that can be selected for a config.
 * @returns {Promise<Response[]>} A promise that resolves to an array of Response objects.
 */
export async function getAllAvailableResponses(): Promise<Response[]> {
  try {
    const response = await Axios.get(
      `${API_URL}/api/company/tblresponses`,
      AxiosConfig.configValue("min")
    );
    return response.data;
  } catch (error) {
    console.error("getAllAvailableResponses/api ~ error:", error);
    throw new StatusError("Unable to fetch data.", 500);
  }
}

/**
 * Disable the specified FootageRestrictedLocateConfigData row. Separate API call so backend can properly fill in 'DeactivatedBy'.
 * @param {number} Id - The ID of the config to disable.
 * @returns {Promise<number>} A promise that resolves to the HTTP status code.
 */
export async function disableFootageRestrictedLocateConfigData(
  Id: number
): Promise<number> {
  try {
    const response = await Axios.delete(
      `${API_URL}/api/company/footagerestrictedlocateconfigs/${Id}`,
      AxiosConfig.configValue("min")
    );
    return response.status;
  } catch (error) {
    console.error(
      "disableFootageRestrictedLocateConfigData/api ~ error:",
      error
    );
    throw new StatusError("Unable to mark config inactive.", 500);
  }
}

/**
 * Enable the specified FootageRestrictedLocateConfigData row. Separate api call so backend can properly fill in 'ModifiedBy'.
 * @param {number} Id - The ID of the config to enable.
 * @returns {Promise<number>} A promise that resolves to the HTTP status code.
 */
export async function enableFootageRestrictedLocateConfigData(
  Id: number
): Promise<number> {
  try {
    const response = await Axios.patch(
      `${API_URL}/api/company/footagerestrictedlocateconfigs/${Id}/enable`,
      {},
      AxiosConfig.configValue("min")
    );
    return response.status;
  } catch (error) {
    console.error(
      "enableFootageRestrictedLocateConfigData/api ~ error:",
      error
    );
    throw new StatusError("Unable to mark config active.", 500);
  }
}

/**
 * Fetch all utilities currently active utilities.
 * @returns {Promise<Utility[]>} A promise that resolves to an array of Utility objects.
 */
export async function getAllUtilities(): Promise<Utility[]> {
  try {
    const response = await Axios.get(
      `${API_URL}/api/utilities`,
      AxiosConfig.configValue("min")
    );
    return response.data;
  } catch (error) {
    console.error("getAllUtilities/api ~ error:", error);
    throw new StatusError("Unable to fetch data.", 500);
  }
}

/**
 * 
 */
export async function getContractsByUtilityId(utilityId: Number): Promise<any> {
  try {
    const response = await Axios.get(
      `${API_URL}/api/company/getContractsByUtilityId/${utilityId}`,AxiosConfig.configValue('min')
    );
    return response.data;
  } catch (error) {
    console.error('getContracts/api ~ error: ', error);
    throw new StatusError('Unable to fetch data.', 500);
  }
}